import * as _ from 'underscore';

import { Module } from '../Module';

// NOTE: This is only a subset of specs that are needed in VizPortal
export interface IOpenWindowSpecs {
  width?: number;
  height?: number;
  toolbar?: number;
  resizable?: number;
  top?: number;
  left?: number;
}

/**
 * Contains window.location behavior not covered by angular's $location service.
 *
 * Why? The browser's window.location cannot be mocked. This adds one level of
 * indirection between app code and window.location so we can mock for testing.
 */
export class WindowLocationService {

  public static $inject = ['$window'];

  constructor(private $window: ng.IWindowService) {
  }

  // Download the file at the uri
  public download(uri: string) {
    this.navigateTo(uri);
  }

  private formatWindowOpenSpecs(specs: IOpenWindowSpecs): string {
    return _.map(<_.Dictionary<any>> specs, (val: any, key: string) => (key + '=' + val)).join(',');
  }

  // Open the given uri in a new window
  public open(uri: string, windowName: string = '_blank', specs?: IOpenWindowSpecs) {
    return this.$window.open(uri, windowName, this.formatWindowOpenSpecs(specs));
  }

  /**
   * Sets the location to the given uri
   */
  public navigateTo(uri: string) {
    this.$window.location.href = uri;
  }

  /**
   * Set the location of the given iframe
   */
  public navigateIFrameTo(iframe: HTMLIFrameElement, uri: string) {
    iframe.contentWindow.location.href = uri;
  }

  public navigateToWithoutAddingToHistory(uri: string) {
    this.$window.location.replace(uri);
  }

  /**
   * Cross-browser method for getting window.location.origin. Example:
   * window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
   * return value         : 'scheme://domain:port'
   */
  public getOrigin() {
    return this.$window.location.protocol + '//' + this.$window.location.host;
  }

  /**
   * Get everything after the host in a url.  Example:
   *
   *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
   *    return value         : '/path?query_string#fragment_id'
   */
  public getPathQueryFragment() {
    return this.$window.location.href.split(this.$window.location.host)[1];
  }

  /**
   * Get path and query components of url.  Example:
   *
   *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
   *    return value         : '/path?query_string'
   */
  public getPathQuery() {
    return this.getPathQueryFragment().replace(this.$window.location.hash, '');
  }

  /**
   * Set everything after the host in a url. Example:
   *
   *    pathQueryFragment    : '/path?query_string#fragment_id'
   *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
   */
  public setPathQueryFragment(pathQueryFragment: string) {
    this.$window.location.href = this.getOrigin() + (/^\//.test(pathQueryFragment) ? '' : '/') + pathQueryFragment;
  }

  public reload() {
    this.$window.location.reload();
  }

  /**
   * Wrapper to stub for unit tests
   */
  public getHost(){
    return this.$window.location.host;
  }

  /**
   * Wrapper to stub for unit tests
   */
  public getProtocol(){
    return this.$window.location.protocol;
  }

  /**
   * Wrapper to stub for unit tests
   */
  public getHref() {
    return this.$window.location.href;
  }

}

Module.instance().service('WindowLocationService', WindowLocationService);
